const S3_RECORDINGS_BASE =
  "https://anyonepray-recordings.s3.amazonaws.com/voice_recording";
const DEFAULT_TWILIO_ACCOUNT_SID = "AC1a7846fb005926a8ff9eeefe4785715e";
const TWILIO_RECORDING_URL_REGEX =
  /api\.twilio\.com\/2010-04-01\/Accounts\/(AC[a-zA-Z0-9]+)\/Recordings\/(RE[a-zA-Z0-9]+)/i;

/**
 * Resolve a call/voicemail recording URL for playback.
 * Twilio API recording URLs are mapped to S3; other http(s) URLs are returned as-is.
 * @returns {string|null} Playable URL, or null when no recording is available
 */
export function resolveRecordingUrl(url, recordingSid) {
  const trimmedUrl = typeof url === "string" ? url.trim() : "";

  if (trimmedUrl && !trimmedUrl.includes("api.twilio.com")) {
    return trimmedUrl;
  }

  let accountSid = DEFAULT_TWILIO_ACCOUNT_SID;
  let sid =
    typeof recordingSid === "string" ? recordingSid.trim() : "";

  if (trimmedUrl) {
    const match = trimmedUrl.match(TWILIO_RECORDING_URL_REGEX);
    if (!match) {
      return null;
    }
    accountSid = match[1];
    sid = sid || match[2];
  }

  if (!sid) {
    return null;
  }

  sid = sid.replace(/\.(json|mp3|wav|ogg)$/i, "");
  return `${S3_RECORDINGS_BASE}/${accountSid}/${sid}`;
}

/** True if value is the literal caller id "Anonymous" (case-insensitive, trimmed). */
export function isAnonymousCallerNumber(value) {
  return typeof value === "string" && value.trim().toLowerCase() === "anonymous";
}

export function formatDate(date){
    const year = date.getFullYear();
    const month = ('0' + (date.getMonth() + 1)).slice(-2); // Months are zero-indexed
    const day = ('0' + date.getDate()).slice(-2);
    const hours = ('0' + date.getHours()).slice(-2);
    const minutes = ('0' + date.getMinutes()).slice(-2);
    const seconds = ('0' + date.getSeconds()).slice(-2);

    // Format as YYYY-MM-DD HH:MM:SS
    const formattedDate = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    return formattedDate; // Outputs local time, e.g., 2024-09-01 00:00:00
}

export function convertSeconds(totalSeconds) {
    const hours = Math.floor(totalSeconds / 3600);  // 1 hour = 3600 seconds
    const minutes = Math.floor((totalSeconds % 3600) / 60);  // Remaining minutes
    const seconds = totalSeconds % 60;  // Remaining seconds after minutes are extracted

    return {
        hours: String(hours).padStart(2, '0'),
        minutes: String(minutes).padStart(2, '0'),
        seconds: String(seconds).padStart(2, '0')
    };
}

/**
 * Safari-compatible date parsing function
 * Handles various date formats and provides fallbacks for Safari's stricter date parsing
 * @param {string|Date} dateInput - The date to parse
 * @returns {Date} - A valid Date object
 */
export function parseDateSafari(dateInput) {
    if (!dateInput) return new Date();
    
    // If it's already a valid Date object, return it
    if (dateInput instanceof Date && !isNaN(dateInput.getTime())) {
        return dateInput;
    }
    
    try {
        let dateString = dateInput.toString();
        
        // Remove " UTC" suffix if present
        dateString = dateString.replace(/\s+UTC$/, '');
        
        let parsedDate;
        
        // Try parsing as ISO string first (most reliable)
        if (dateString.includes('T') || dateString.includes('Z')) {
            parsedDate = new Date(dateString);
        } else if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
            // Handle YYYY-MM-DD format
            parsedDate = new Date(dateString + 'T00:00:00');
        } else if (dateString.match(/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2}$/)) {
            // Handle YYYY-MM-DD HH:MM:SS format - parse as local time
            const parts = dateString.split(' ');
            const datePart = parts[0];
            const timePart = parts[1];
            const [year, month, day] = datePart.split('-').map(Number);
            const [hours, minutes, seconds] = timePart.split(':').map(Number);
            // Create date in local timezone
            parsedDate = new Date(year, month - 1, day, hours, minutes, seconds || 0);
        } else {
            // Try direct parsing as fallback
            parsedDate = new Date(dateString);
        }
        
        // Validate the parsed date
        if (isNaN(parsedDate.getTime())) {
            console.warn('Invalid date string, falling back to current date:', dateInput);
            return new Date();
        }
        
        return parsedDate;
    } catch (error) {
        console.error('Date parsing error:', error, 'Input:', dateInput);
        return new Date();
    }
}

/**
 * Safari-compatible date formatting function
 * Ensures consistent date formatting across browsers
 * @param {Date} date - The date to format
 * @param {string} format - The format type ('iso', 'display', 'time')
 * @returns {string} - Formatted date string
 */
export function formatDateSafari(date, format = 'iso') {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
        return '';
    }
    
    try {
        switch (format) {
            case 'iso':
                return date.toISOString();
            case 'display':
                return date.toLocaleDateString();
            case 'time':
                return date.toLocaleTimeString();
            case 'datetime':
                return date.toLocaleString();
            default:
                return date.toISOString();
        }
    } catch (error) {
        console.error('Date formatting error:', error);
        return '';
    }
}

/**
 * Detect if the current browser is Safari
 * @returns {boolean} - True if Safari, false otherwise
 */
export function isSafari() {
    const userAgent = navigator.userAgent;
    return /^((?!chrome|android).)*safari/i.test(userAgent);
}

/**
 * Safari-compatible error handler for date operations
 * @param {Function} operation - The operation to perform
 * @param {*} fallback - The fallback value if operation fails
 * @returns {*} - Result of operation or fallback value
 */
export function safeDateOperation(operation, fallback = null) {
    try {
        return operation();
    } catch (error) {
        if (isSafari()) {
            console.warn('Safari date operation failed, using fallback:', error);
        } else {
            console.error('Date operation failed:', error);
        }
        return fallback;
    }
}


// Function to generate and download an Excel file
export function downloadResourcesExcel(headers, data, filename = "Resources-"+formatDate(new Date())+".xlsx") {
  if (!Array.isArray(headers) || !Array.isArray(data)) {
    console.error("Headers and data must be arrays.");
    return;
  }

  // Import XLSX dynamically to avoid SSR issues
  import('xlsx').then((XLSX) => {
    // Create worksheet
    const worksheet = XLSX.utils.json_to_sheet(
      data.map(row => {
        const newRow = {};
        headers.forEach(header => {
          if (header.includes("Postal Code")) {
            newRow[header] = row[header.replace(" ", "_")] ?? "";
          } else {
            newRow[header] = row[header] ?? "";
          }
        });
        return newRow;
      })
    );

    // Create workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

    // Generate Excel file
    XLSX.writeFile(workbook, filename);
  }).catch(error => {
    console.error("Error generating Excel file:", error);
  });
}

/**
 * Get all users for a specific organization from backend API
 * @param {string} organizationName - The organization name to filter by
 * @returns {Promise<Array>} Array of users for the organization
 */
export const getUsersByOrganization = async (organizationName) => {
    try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/auth0/users?organization=${encodeURIComponent(organizationName)}`, {
            headers: {
                'Authorization': process.env.REACT_APP_API_AUTHORIZATION,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            throw new Error(`Failed to fetch users: ${response.status}`);
        }

        const data = await response.json();
        return data.users || [];
    } catch (error) {
        console.error("Error fetching users by organization:", error);
        throw error;
    }
};

/**
 * Get all users with specific criteria - Backend API version
 * @param {string} organizationName - The organization name to filter by
 * @param {string} role - The role to filter by (default: "subscriber")
 * @returns {Promise<Array>} Array of users matching the criteria
 */
export const getUsersByOrganizationAndRole = async (organizationName, role = "subscriber") => {
    try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/auth0/users?organization=${encodeURIComponent(organizationName)}&role=${encodeURIComponent(role)}`, {
            headers: {
                'Authorization': process.env.REACT_APP_API_AUTHORIZATION,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            throw new Error(`Failed to fetch users: ${response.status}`);
        }

        const data = await response.json();
        return data.users || [];
    } catch (error) {
        console.error("Error fetching users by organization and role:", error);
        throw error;
    }
};

/**
 * Get all users with specific criteria using pagination - Backend API version
 * @param {string} organizationName - The organization name to filter by
 * @param {string} role - The role to filter by (default: "subscriber")
 * @param {number} page - Page number (default: 0)
 * @param {number} perPage - Number of users per page (default: 100)
 * @returns {Promise<Array>} Array of users matching the criteria with pagination info
 */
export const getUsersByOrganizationAndRolePaginated = async (
    organizationName, 
    role = "subscriber", 
    page = 0, 
    perPage = 100
) => {
    try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/auth0/users?organization=${encodeURIComponent(organizationName)}&role=${encodeURIComponent(role)}&page=${page}&per_page=${perPage}`, {
            headers: {
                'Authorization': process.env.REACT_APP_API_AUTHORIZATION,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            throw new Error(`Failed to fetch users: ${response.status}`);
        }

        const data = await response.json();
        return {
            users: data.users || [],
            total: data.total || 0,
            start: data.start || 0,
            limit: data.limit || perPage,
            length: data.length || 0
        };
    } catch (error) {
        console.error("Error fetching users by organization and role:", error);
        throw error;
    }
};
